import {
  ChangeDetectionStrategy,
  Component,
  PLATFORM_ID,
  computed,
  inject,
  linkedSignal,
  signal,
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DecimalPipe, isPlatformBrowser } from '@angular/common';
import { catchError, of } from 'rxjs';

import { TranslatePipe } from '@app/pipes/translate.pipe';
import { FormatCurrencyPipe } from '@app/pipes/format-currency.pipe';
import { AvatarComponent } from '@app/components/ui-components/avatar/avatar.component';
import { StarsComponent } from '@app/components/ui-components/stars/stars.component';
import { IconComponent } from '@app/components/ui-icon/icon.component';
import {
  isIconName,
  type IconName,
} from '@app/components/ui-icon/icon-registry';
import { ExternalService } from '@app/services/http/external.service';
import { LocalizationService } from '@services/logic/localization.service';
import type {
  ReviewSliderCard,
  ReviewSliderCluster,
  ReviewSliderPayload,
} from '@models/review-slider.model';

interface ReviewCard extends ReviewSliderCard {
  url: string;
  // Repeats added to fill a short lane; hidden from assistive tech and the tab order,
  // like the duplicate copy.
  isPadding: boolean;
}

// Below this many cards one copy can be narrower than the viewport and the loop shows a gap.
const MINIMUM_CARDS_PER_COPY = 6;
// Below this many cards a second lane would just repeat one or two cards.
const MINIMUM_CARDS_FOR_TWO_LANES = 4;
// Constant scroll speed regardless of how many cards a lane holds.
const SECONDS_PER_CARD = 8;

// Cluster icons that handyhand-web takes from hugeicons-pro v4 but this
// project's v3 does not ship.
const ICON_FALLBACKS: Record<string, IconName> = {
  drill: 'wrench-01',
  'brick-wall': 'building-05',
};

/**
 * Port of handyhand-web's `app-review-slider` (task-owner dashboard). Same
 * markup, styles and lane logic; the data differs.
 *
 * handyhand-web fetches clusters, then each cluster's reviews with full
 * `fromUser,toUser,offer,offer.task` includes — ~63 KB per cluster, nine
 * requests, two in series before the first card. Here one request to this
 * project's own `/api/review-slider` returns every cluster already projected
 * to card fields and cached server-side (serverUtils/reviewSlider.ts), so the
 * first paint waits on one small response and switching pills never fetches.
 */
@Component({
  selector: 'app-review-slider',
  templateUrl: './review-slider.component.html',
  styleUrls: ['./review-slider.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [TranslatePipe],
  imports: [
    TranslatePipe,
    FormatCurrencyPipe,
    AvatarComponent,
    IconComponent,
    StarsComponent,
    DecimalPipe,
  ],
})
export class ReviewSliderComponent {
  private readonly http = inject(HttpClient);
  private readonly externalService = inject(ExternalService);
  private readonly localizationService = inject(LocalizationService);
  private readonly translate = inject(TranslatePipe);
  protected readonly statistics = this.localizationService.statistics;

  // null until loaded; [] when the endpoint failed or had nothing to show.
  protected readonly clusters = signal<ReviewSliderCluster[] | null>(null);

  protected readonly selectedClusterId = linkedSignal<
    ReviewSliderCluster[] | null,
    string | null
  >({
    source: this.clusters,
    computation: (clusters, previous) =>
      clusters?.some((cluster) => cluster.id === previous?.value)
        ? (previous?.value ?? null)
        : (clusters?.[0]?.id ?? null),
  });

  protected readonly lanes = computed<ReviewCard[][] | null>(() => {
    const clusters = this.clusters();

    if (clusters === null) {
      return null;
    }

    const selected = clusters.find(
      (cluster) => cluster.id === this.selectedClusterId(),
    );
    const cards = (selected?.cards ?? []).map((card) => this.toCard(card));

    if (cards.length === 0) {
      return [];
    }

    const middle = Math.ceil(cards.length / 2);
    const lanes =
      cards.length >= MINIMUM_CARDS_FOR_TWO_LANES
        ? [cards.slice(0, middle), cards.slice(middle)]
        : [cards];

    return lanes.map((lane) => {
      const padded = [...lane];
      while (padded.length > 0 && padded.length < MINIMUM_CARDS_PER_COPY) {
        padded.push(...lane.map((card) => ({ ...card, isPadding: true })));
      }
      return padded;
    });
  });

  private readonly dateFormat = computed(
    () =>
      new Intl.DateTimeFormat(this.localizationService.bcp47(), {
        dateStyle: 'medium',
      }),
  );

  constructor() {
    // The slider sits in an `@defer (on viewport)` block, so it never renders
    // during SSR; the guard keeps a relative-URL request off the server anyway.
    if (!isPlatformBrowser(inject(PLATFORM_ID))) {
      return;
    }

    this.http
      .get<ReviewSliderPayload>('/api/review-slider')
      .pipe(
        catchError(() => of<ReviewSliderPayload>({ clusters: [] })),
        takeUntilDestroyed(),
      )
      .subscribe(({ clusters }) => this.clusters.set(clusters));
  }

  protected select(clusterId: string): void {
    this.selectedClusterId.set(clusterId);
  }

  protected iconName(cluster: ReviewSliderCluster): IconName {
    const icon = cluster.icon ?? '';
    return (
      ICON_FALLBACKS[icon] ?? (isIconName(icon) ? icon : 'more-horizontal')
    );
  }

  protected laneDuration(lane: ReviewCard[]): string {
    return `${lane.length * SECONDS_PER_CARD}s`;
  }

  protected ratingLabel(rating: number): string {
    return this.translate
      .transform('PAGES.LANDING.REVIEWS_RATING_LABEL')
      .replace('{{rating}}', String(rating));
  }

  protected formatDate(value: string): string {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '' : this.dateFormat().format(date);
  }

  private toCard(card: ReviewSliderCard): ReviewCard {
    return {
      ...card,
      url: this.externalService.taskUrl(card.taskSlug, card.taskId),
      isPadding: false,
    };
  }
}
